// React Query hooks for contract-level ACL grants — fetch the access list,
// grant a user, revoke a user. Mutations invalidate both the per-contract
// access query and the parent managed-contracts queries so list rows
// (including the owner/manager auto-grant fan-out) refresh on the contract
// detail page after a grant change.

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { contractAclApi } from "../services/api";
import { useAuth } from "../contexts/AuthContext";

export function useContractAccess(contractId: string) {
  return useQuery({
    queryKey: ["contract-access", contractId],
    queryFn: () => contractAclApi.get(contractId),
    enabled: !!contractId,
  });
}

export function useGrantContractAcl() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      contractId,
      userId,
    }: {
      contractId: string;
      userId: string;
    }) => contractAclApi.grant(contractId, userId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["contract-access", variables.contractId],
      });
      queryClient.invalidateQueries({ queryKey: ["managed-contracts"] });
      queryClient.invalidateQueries({
        queryKey: ["user-contract-grants"],
      });
    },
  });
}

// Lists the contracts the target user holds ACL grants on, hydrated with
// contractNumber + title. Pass undefined to disable the query (e.g. when
// the user has 'all' access — no per-contract grants are relevant).
export function useUserContractGrants(userId: string | undefined) {
  const { user } = useAuth();
  const orgId = user?.organisationId;
  return useQuery({
    queryKey: ["user-contract-grants", orgId, userId],
    queryFn: () => contractAclApi.listGrantsForUser(orgId!, userId!),
    enabled: !!orgId && !!userId,
  });
}

export function useRevokeContractAcl() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      contractId,
      userId,
    }: {
      contractId: string;
      userId: string;
    }) => contractAclApi.revoke(contractId, userId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["contract-access", variables.contractId],
      });
      queryClient.invalidateQueries({ queryKey: ["managed-contracts"] });
      queryClient.invalidateQueries({
        queryKey: ["user-contract-grants"],
      });
    },
  });
}
